import { t } from '../../../i18n';

function isEditableElement(target: EventTarget | null): boolean {
  const element = target instanceof HTMLElement ? target : null;
  if (!element) {
    return false;
  }
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
    return true;
  }
  return element.isContentEditable || element.closest('input, textarea, select, [contenteditable="true"]') !== null;
}

export function stopEditingEventPropagation(element: HTMLElement): void {
  const stopIfEditing = (event: Event): void => {
    if (!isEditableElement(event.target)) {
      return;
    }
    event.stopPropagation();
  };
  element.addEventListener('keydown', stopIfEditing, true);
  element.addEventListener('keypress', stopIfEditing, true);
  element.addEventListener('keyup', stopIfEditing, true);
}

export function buttonStyle(background: string): Partial<CSSStyleDeclaration> {
  return {
    width: '100%',
    padding: '8px 10px',
    borderRadius: '6px',
    border: 'none',
    background,
    color: '#f8fafc',
    cursor: 'pointer',
  };
}

export function createSection(titleKey: string): HTMLDivElement {
  const section = document.createElement('div');
  Object.assign(section.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px',
    borderRadius: '8px',
    background: 'rgba(8, 15, 24, 0.58)',
    border: '1px solid rgba(148, 163, 184, 0.16)',
  });

  const title = document.createElement('h4');
  title.textContent = t(titleKey);
  Object.assign(title.style, {
    margin: '0',
    color: '#e2e8f0',
    fontSize: '14px',
  });

  section.appendChild(title);
  return section;
}

export function createSectionLabel(title: string): HTMLDivElement {
  const section = document.createElement('div');
  Object.assign(section.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px',
    borderRadius: '8px',
    background: 'rgba(8, 15, 24, 0.58)',
    border: '1px solid rgba(148, 163, 184, 0.16)',
  });

  const heading = document.createElement('h4');
  heading.textContent = title;
  Object.assign(heading.style, {
    margin: '0',
    color: '#e2e8f0',
    fontSize: '14px',
  });

  section.appendChild(heading);
  return section;
}

export function createAdminInput(
  label: string,
  value: string,
  onInput: (value: string) => void,
  type: 'text' | 'number' = 'text'
): HTMLDivElement {
  const wrapper = document.createElement('div');
  Object.assign(wrapper.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: '1',
  });

  const labelEl = document.createElement('label');
  labelEl.textContent = label;
  Object.assign(labelEl.style, {
    color: '#cbd5e1',
    fontSize: '12px',
  });

  const input = document.createElement('input');
  input.type = type;
  input.value = value;
  Object.assign(input.style, {
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    background: 'rgba(15, 23, 42, 0.75)',
    color: '#f8fafc',
  });
  input.addEventListener('input', (event) => {
    onInput((event.target as HTMLInputElement).value);
  });
  input.addEventListener('keydown', (event) => event.stopPropagation());
  input.addEventListener('keypress', (event) => event.stopPropagation());
  input.addEventListener('keyup', (event) => event.stopPropagation());

  wrapper.appendChild(labelEl);
  wrapper.appendChild(input);
  return wrapper;
}

export function createMapSelect(
  currentValue: string,
  onChange: (value: string) => void,
  maps: Array<{ mapId: string; name: string }>
): HTMLDivElement {
  const wrapper = document.createElement('div');
  Object.assign(wrapper.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  });

  const labelEl = document.createElement('label');
  labelEl.textContent = 'Map';
  Object.assign(labelEl.style, {
    color: '#cbd5e1',
    fontSize: '12px',
  });

  const select = document.createElement('select');
  Object.assign(select.style, {
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    background: 'rgba(15, 23, 42, 0.75)',
    color: '#f8fafc',
  });
  select.addEventListener('change', (event) => {
    onChange((event.target as HTMLSelectElement).value);
  });
  select.addEventListener('keydown', (event) => event.stopPropagation());
  select.addEventListener('keypress', (event) => event.stopPropagation());
  select.addEventListener('keyup', (event) => event.stopPropagation());

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = maps.length > 0 ? 'Select a map' : 'No maps available';
  select.appendChild(placeholder);

  for (const map of maps) {
    const option = document.createElement('option');
    option.value = map.mapId;
    option.textContent = `${map.mapId} (${map.name})`;
    if (map.mapId === currentValue) {
      option.selected = true;
    }
    select.appendChild(option);
  }

  if (!currentValue) {
    select.value = '';
  }

  wrapper.appendChild(labelEl);
  wrapper.appendChild(select);
  return wrapper;
}
